import type { BridgePrompt } from '@entangled-vega/shared';

export type StepGuessRange = '1-2' | '3-4' | '5-plus';

type FeaturedGuessProps = {
  fromName: string;
  toName: string;
  selectedGuess: StepGuessRange | null;
  bridgePrompt: BridgePrompt | null;
  selectedBridgeChoiceId: string | null;
  onGuess: (guess: StepGuessRange) => void;
  onBridgeGuess: (personId: string) => void;
  onReveal: () => void;
};

export function guessMatchesStepCount(guess: StepGuessRange, stepCount: number): boolean {
  return guess === '1-2'
    ? stepCount <= 2
    : guess === '3-4'
      ? stepCount >= 3 && stepCount <= 4
      : stepCount >= 5;
}

export function FeaturedGuess({
  fromName,
  toName,
  selectedGuess,
  bridgePrompt,
  selectedBridgeChoiceId,
  onGuess,
  onBridgeGuess,
  onReveal,
}: FeaturedGuessProps) {
  return (
    <section className="featured-guess" aria-label="Guess the relationship path length">
      <p className="eyebrow">Guess before the reveal</p>
      <h3>
        How many relationship steps connect {fromName} and {toName}?
      </h3>
      <div className="featured-guess__options" role="group" aria-label="Choose a step range">
        {(
          [
            ['1-2', '1–2 steps'],
            ['3-4', '3–4 steps'],
            ['5-plus', '5+ steps'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            className="button button--small"
            type="button"
            aria-pressed={selectedGuess === value}
            onClick={() => onGuess(value)}
          >
            {label}
          </button>
        ))}
      </div>
      {bridgePrompt === null ? null : (
        <div className="featured-guess__bridge">
          <h3>Which person appears inside this accepted path?</h3>
          <div className="featured-guess__options" role="group" aria-label="Choose a bridge person">
            {bridgePrompt.choices.map((choice) => (
              <button
                key={choice.id}
                className="button button--small"
                type="button"
                aria-pressed={selectedBridgeChoiceId === choice.id}
                onClick={() => onBridgeGuess(choice.id)}
              >
                {choice.name}
              </button>
            ))}
          </div>
          <p>
            Choices come from accepted featured paths. An unselected choice makes no claim about a
            real-world relationship.
          </p>
        </div>
      )}
      <button className="button button--gold" type="button" onClick={onReveal}>
        {selectedGuess === null && selectedBridgeChoiceId === null
          ? 'Reveal now'
          : 'Reveal the source-linked path'}
      </button>
      <p>This guess stays only in this page session and is never sent or saved.</p>
    </section>
  );
}
